import { useState } from 'react';
import styled from 'styled-components';
import { apiErrorText, useSetFieldElementStatusMutation } from '@shared/api';
import { elementStatusActions, type ElementStatusAction, type FieldElement } from '@entities/field';
import { Button } from '@shared/ui/Button';

const Row = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 8px;
`;

const Note = styled.div<{ $ok: boolean }>`
  font-size: 13px;
  margin-top: 6px;
  color: ${({ theme, $ok }) => ($ok ? theme.colors.status.success : theme.colors.status.danger)};
`;

/** Подпись элемента для подтверждения: «код — наименование». */
function elementLabel(el: Pick<FieldElement, 'code' | 'title'>): string {
  return el.code ? `${el.code} — ${el.title}` : el.title;
}

/**
 * Ручной статус элемента конструктива (КR7) — только проверяющему ПТО площадки:
 * «Отметить элемент принятым» (программа «принят» не ставит: одна пройденная
 * заявка покрывает часть элемента) и «Вернуть автоматический статус» (статус
 * снова считается по заявкам и фактам). Сервер — `POST /elements/{id}/status`.
 */
export function ElementStatusControls({
  element,
  reviewer,
}: {
  element: FieldElement;
  reviewer: boolean;
}) {
  const [setStatus, { isLoading: saving }] = useSetFieldElementStatusMutation();
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const actions = elementStatusActions(element, reviewer);
  if (actions.length === 0 && !note) return null;

  async function run(a: ElementStatusAction) {
    if (
      a.status === 'accepted' &&
      !window.confirm(
        `Отметить элемент «${elementLabel(element)}» принятым целиком? Статус станет ручным — ` +
          'программа перестанет его менять, пока вы не вернёте автоматический.',
      )
    ) {
      return;
    }
    setNote(null);
    try {
      await setStatus({ elementId: element.id, status: a.status }).unwrap();
      setNote({
        ok: true,
        text:
          a.status === 'accepted'
            ? 'Элемент отмечен принятым (статус ручной).'
            : 'Статус снова ведёт программа — он пересчитан по заявкам и фактам элемента.',
      });
    } catch (e) {
      setNote({ ok: false, text: apiErrorText(e, 'Не удалось изменить статус элемента.') });
    }
  }

  return (
    <>
      {actions.length > 0 && (
        <Row>
          {actions.map((a) => (
            <Button
              key={a.status}
              type="button"
              $variant={a.status === 'accepted' ? 'secondary' : 'ghost'}
              $size="sm"
              disabled={saving}
              title={a.title}
              onClick={() => void run(a)}
            >
              {a.label}
            </Button>
          ))}
        </Row>
      )}
      {note && <Note $ok={note.ok}>{note.text}</Note>}
    </>
  );
}
